import { Action } from 'redux';
import { put } from '@redux-saga/core/effects';

import { CheckoutActions, CheckoutActionTypes } from '../checkout.types';
import { ACTION_CREATOR_NAME, analyticsHelper, errorToBasketErrorModel, userHelper } from 'helpers';
import { ErrorAction } from '../../root.types';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';
import { BasketErrorModel, DEFAULT_ERROR_TEXT } from 'models/Errors';
import { transferBasketSaga } from './transferBasket';
import { api } from 'repositories/api';

export interface InitCompleteResetPasswordPageAction extends Action<CheckoutActionTypes.INIT_COMPLETE_RESET_PASSWORD_PAGE> {
    basketId: string | null;
}

export interface InitCompleteResetPasswordPageActionStart extends Action<CheckoutActionTypes.INIT_COMPLETE_RESET_PASSWORD_PAGE_START> {
}

export interface InitCompleteResetPasswordPageActionSuccess extends Action<CheckoutActionTypes.INIT_COMPLETE_RESET_PASSWORD_PAGE_SUCCESS> {
}

export interface InitCompleteResetPasswordPageActionError extends ErrorAction<CheckoutActionTypes.INIT_COMPLETE_RESET_PASSWORD_PAGE_ERROR> {
}

export const initCompleteResetPasswordPage: (basketId: string | null) => InitCompleteResetPasswordPageAction = (basketId: string | null) => ({
    type: CheckoutActionTypes.INIT_COMPLETE_RESET_PASSWORD_PAGE,
    basketId
});
initCompleteResetPasswordPage[ACTION_CREATOR_NAME] = 'initCompleteResetPasswordPage';

export type InitCompleteResetPasswordPageFunc = (...args: Parameters<typeof initCompleteResetPasswordPage>) => void;

export function* initCompleteResetPasswordPageSaga({ basketId }: InitCompleteResetPasswordPageAction): BasketSagaReturnType {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.INIT_COMPLETE_RESET_PASSWORD_PAGE_START
    });

    try {
        if (!userHelper.isUserLoggedIn()) {
            throw new BasketErrorModel(
                DEFAULT_ERROR_TEXT,
                'User is not signed in',
                'initCompleteResetPasswordPageSaga'
            );
        }

        if (!basketId) {
            throw new BasketErrorModel(
                DEFAULT_ERROR_TEXT,
                'BasketId does not exist',
                'initCompleteResetPasswordPageSaga'
            );
        }

        const sessionId = userHelper.getSessionId();
        if (sessionId) {
            // if sessionId exists - this means that this page is opened inside react application routes flow - not by redirect from external page
            throw new BasketErrorModel(
                DEFAULT_ERROR_TEXT,
                `SessionId should not exists in complete restore password process [sessionId: ${sessionId}]`,
                'initCompleteResetPasswordPageSaga'
            );
        }

        yield callBasketSaga(transferBasketSaga, {
            type: CheckoutActionTypes.TRANSFER_BASKET,
            basketId
        });

        const user = userHelper.getUser();

        let userId = user?.userId
            ? user.userId
            : userHelper.getRetailExperienceUser()?.userId;

        if (userId) {
            sendAnalytics(userId);
        }

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.INIT_COMPLETE_RESET_PASSWORD_PAGE_SUCCESS
        });

        return true;
    } catch (error) {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.INIT_COMPLETE_RESET_PASSWORD_PAGE_ERROR,
            error: errorToBasketErrorModel(error, 'initCompleteResetPasswordPageSaga')
        });

        return false;
    }
}

function sendAnalytics(userId: string): void {
    analyticsHelper.withLogging(() => {
        api.exponea.identify(userId);
    }, 'initCompleteResetPasswordPageSaga');
}
